/**
 * Pieces shared by the invitee screens: the centred status card (expired /
 * report / sign-in screens), the "password set" card that replaces the
 * PasswordCard once the account exists, the accept-error note, and small
 * display helpers.
 */
import type React from "react";
import { useTranslation } from "react-i18next";
import { Check, KeyRound } from "lucide-react";
import { Badge, Ic, Note, ObIcons, TopBar, currentObLocale, type InvitationOrg, type LucideIcon } from "../kit";
import type { CreateAccountOutcome } from "./session";

/** The org's name in the active language (Arabic name when set). */
export function orgDisplayName(org: InvitationOrg | null | undefined, fallback?: string | null, lang = currentObLocale()): string {
  if (org) return lang === "ar" && org.name_ar ? org.name_ar : org.name;
  return fallback ?? "";
}

export function firstName(name: string | null | undefined): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

/** The design's TopBar for the invitee screens: org name + the person's name badge. */
export function InviteeTopBar({ orgName, personName }: { orgName?: string | null; personName?: string | null }) {
  return (
    <TopBar
      orgName={orgName || null}
      mid={
        personName ? (
          <Badge kind="neu" icon={ObIcons.user}>
            {personName}
          </Badge>
        ) : undefined
      }
    />
  );
}

export type StatusTone = "warn" | "ok" | "bad" | "info";

const TONE: Record<StatusTone, React.CSSProperties> = {
  warn: { background: "rgba(245,158,11,.16)", color: "#8A3A05" },
  ok: { background: "rgba(16,185,129,.14)", color: "#065F46" },
  bad: { background: "rgba(220,38,38,.12)", color: "#991B1B" },
  info: {},
};

/** Centred glass card (design: ScreenExpired / EmpExpired). */
export function StatusCard({
  tone = "warn",
  icon,
  title,
  children,
  actions,
  label,
}: {
  tone?: StatusTone;
  icon: LucideIcon;
  title: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  /** data-screen-label, for debugging / tests. */
  label?: string;
}) {
  return (
    <main className="ob-wrap ob-anim" style={{ maxWidth: 520, marginTop: "clamp(24px,8vh,80px)" }} data-screen-label={label}>
      <section
        className="ob-glass"
        style={{
          padding: "clamp(24px,5cqi,40px)",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span className="ob-tile" style={{ width: 56, height: 56, borderRadius: "50%", fontSize: 26, ...TONE[tone] }}>
          <Ic icon={icon} weight="bold" />
        </span>
        <h1 className="ob-h2">{title}</h1>
        {children}
        {actions && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginTop: 8 }}>{actions}</div>
        )}
      </section>
    </main>
  );
}

/** Replaces the PasswordCard once the account exists — the password can no longer change here. */
export function PasswordDoneCard({ email }: { email: string }) {
  const { t } = useTranslation();
  return (
    <section
      className="ob-glass"
      style={{ padding: "clamp(20px,3cqi,28px)", display: "flex", flexDirection: "column", gap: 16 }}
      data-testid="password-done"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span className="ob-tile">
          <Ic icon={KeyRound} />
        </span>
        <div>
          <p className="ob-eye">{t("onboarding.kit.step", { defaultValue: "Step {{n}}", n: 1 })}</p>
          <h2 className="ob-h3">{t("onboarding.invitee.secure.passwordSetTitle", "Password set")}</h2>
        </div>
      </div>
      <span>
        <Badge kind="ok" icon={Check}>
          {t("onboarding.invitee.secure.accountCreated", "Account created")}
        </Badge>
      </span>
      <p className="ob-sm" style={{ margin: 0 }}>
        {t("onboarding.invitee.secure.signInAs", "You'll sign in as")}{" "}
        <strong dir="ltr" style={{ color: "var(--ink)" }}>
          {email}
        </strong>
      </p>
    </section>
  );
}

/** Note for an accept/sign-in failure that is not a password or link problem. */
export function AccountErrorNote({
  outcome,
  activation,
}: {
  outcome: Extract<CreateAccountOutcome, { status: "error" }>;
  activation?: boolean;
}) {
  const { t } = useTranslation();
  if (outcome.code === "email_taken") {
    return (
      <Note kind="bad" title={t("onboarding.invitee.err.emailTakenTitle", "This address already has an account.")}>
        {t("onboarding.invitee.err.emailTaken", "Sign in with it instead, or ask your administrator to check the invitation.")}{" "}
        <a className="ob-link" href="/login">
          {t("onboarding.invitee.signIn", "Sign in")}
        </a>
      </Note>
    );
  }
  if (outcome.code === "quota_exceeded") {
    return (
      <Note kind="bad" title={t("onboarding.invitee.err.quotaTitle", "No seats left.")}>
        {activation
          ? t("onboarding.invitee.err.quotaActivation", "Your workspace has no free seats. Contact We-Co support to add seats.")
          : t("onboarding.invitee.err.quota", "Your organization has used all its seats. Ask your administrator to free one up.")}
      </Note>
    );
  }
  return (
    <Note kind="bad" title={t("onboarding.invitee.err.genericTitle", "We couldn't set up your account.")}>
      {outcome.detail || t("onboarding.invitee.err.generic", "Check your connection and try again.")}
    </Note>
  );
}
